import {useEffect, useState} from "react";
import {useAppSelector} from "./app/hooks";

/**
 * Default Storage system
 */
export const defaultStorage: Storage = sessionStorage;

/**
 * A custom React hook that stores and synchronizes a value in the browser's storage (localStorage by default).
 * The hook initializes the state with the value stored under the provided key from the specified storage,
 * or the provided default value if no value is stored.
 * It also persists the state changes back to storage automatically.
 *
 * @template Type - The type of the value to be persisted.
 *
 * @param {string} key - The key used to store and retrieve the value in the selected storage.
 * @param {Type} defaultValue - The default value to use if no value is found in storage.
 * @param {Storage} [storage=localStorage] - The browser storage interface to use (localStorage by default).
 *
 * @returns {Type} - The current state value synchronized with the storage.
 */
export function usePersistedValue<Type>(
    key: string,
    defaultValue: Type,
    storage: Storage = defaultStorage,
): Type {

    const [value, setValue] = useState<Type>(() => {
        const storedValue = storage.getItem(key);
        return storedValue !== null ? JSON.parse(storedValue) : defaultValue;
    });

    useEffect(() => {
        storage.setItem(key, JSON.stringify(value));
    }, [key, value, storage]);

    useEffect(() => {
        setValue(defaultValue);
    }, [defaultValue, setValue])

    return value;
}

/**
 * A custom React hook that selects and stores a derived value from the Redux store in the browser's storage (localStorage by default).
 * The hook initializes the state with the value retrieved by applying the selector function to the Redux store,
 * and persists the selected value in the specified storage to ensure the value is synchronized across sessions.
 *
 * @template Type - The type of the selected value to be persisted.
 *
 * @param {string} key - The key used to store and retrieve the selected value in the chosen storage.
 * @param {(state: any) => Type} selector - A function to extract a specific slice of state from the Redux store.
 * @param {Storage} [storage=localStorage] - The browser storage interface to use (localStorage by default).
 *
 * @returns {Type} - The selected value from the Redux store synchronized with the specified storage.
 */
export function usePersistedSelector<Type>(
    key: string,
    selector: (state: any) => Type,
    storage: Storage = defaultStorage,
): Type {
    const value = useAppSelector(selector);
    return usePersistedValue<Type>(key, value, storage);
}

export function getStorageItem<Type>(
    key: string,
    defaultValue: Type,
    storage: Storage = defaultStorage,
): Type {
    const json = storage.getItem(key);
    return json ? JSON.parse(json) : defaultValue;
}